/**
 * RoomRemovableObjects — native staging furniture-removal selector (lifted from
 * `common/StagingFlow/RoomStep/RoomRemovableObjects.component.tsx`, rewired onto
 * the module working context + `patchRoom`).
 *
 * The legacy renovation *price* (`renovationProductStringPrice`) came from
 * the session-keyed renovation-product fetch that backs the DROPPED v2 renovation
 * cart-extra (DF-staging-cart-renovation [REVENUE]). Native staging drops the
 * price line and renders the no-price description variant.
 */

import React, { useCallback, useMemo } from 'react'

import { GRAY_900, YELLOW_600 } from 'constants/styling/theme'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import Stack from '@mui/material/Stack'
import type { ToggleFilterValue } from 'components/common/ToggleFilter'
import Typography from '@mui/material/Typography'
import { VirtualFurnitureRoomRemovableOption } from 'constants/virtualFurniture'
import { useTranslation } from 'react-i18next'

import styles from 'components/common/StagingFlow/RoomStep/RoomStyle.module.sass'

import { useStagingWorkingState } from '../StagingWorkingState.context'
import { useStagingStylesFilter } from '../useStagingStylesFilter'

export const RoomRemovableObjects: React.FC = () => {
  const { t } = useTranslation(['style_selection_order_step', 'virtual_furniture_room_removable_option'])

  const { activeRoom, patchRoom } = useStagingWorkingState()
  const { availableRoomRemovableOptions } = useStagingStylesFilter()

  const roomRemovableOptionToggleValues: ToggleFilterValue<VirtualFurnitureRoomRemovableOption>[] = useMemo(
    () =>
      availableRoomRemovableOptions.map((removableOption) => ({
        value: removableOption,
        displayName: t(`virtual_furniture_room_removable_option:${removableOption}`),
      })),
    [availableRoomRemovableOptions, t]
  )

  const onSelectRemovableObjects = useCallback(
    (item: VirtualFurnitureRoomRemovableOption) => {
      if (!activeRoom) return
      patchRoom(activeRoom.key, { furnitureRemoval: item })
    },
    [activeRoom, patchRoom]
  )

  if (!activeRoom) return null

  return (
    <div>

      <Stack gap={0.4} marginBottom={1.6} textAlign="center">

        <Typography color={GRAY_900} variant="text-lg" fontWeight={600}>
          {t('style_selection_order_step:room_removable_options')}
          &nbsp;
          <span style={{ fontSize: '1.4rem', color: YELLOW_600, fontWeight: 'bold' }}>
            ({t('style_selection_order_step:mandatory')})
          </span>
        </Typography>

      </Stack>

      <SelectableButtonsGrid
        values={roomRemovableOptionToggleValues}
        selectedValue={activeRoom.furnitureRemoval}
        className={styles.roomGridContainer}
        onSelect={onSelectRemovableObjects}
      />

    </div>
  )
}
